// ---------------------------------------------------------------------------
// HTTP client - transport layer shared by all service modules.
// ---------------------------------------------------------------------------

import { isValidId, toIdString } from "@horseed/shared/ids";

export const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/+$/, "");

const ZONE_STORAGE_KEY = "hz_zone_id";

export function getStoredZoneId() {
  try {
    if (typeof window === "undefined") return null;
    const raw = window.localStorage.getItem(ZONE_STORAGE_KEY);
    // Zone ids are GUIDs - the old numeric check rejected every one of them.
    return isValidId(raw) ? toIdString(raw) : null;
  } catch (_) {
    return null;
  }
}

export function authHeader(token) {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function friendlyErrorMessage(message) {
  const text = String(message || "").trim();
  if (!text) return "Something went wrong. Please try again.";

  if (text.toLowerCase() === "incorrect username or password.") {
    return "We couldn't sign you in. Please check your email and password and try again.";
  }

  return text;
}

/**
 * Thin fetch wrapper that:
 *  - Injects zone, content-type, and auth headers.
 *  - Unwraps the API envelope (res.ok check).
 *  - Dispatches `auth:expired` on 401.
 *  - Dispatches `zone:server-echo` when the server echoes zone headers.
 *
 * Returns the raw JSON (including envelope) so callers can access `json.data`.
 */
export async function apiRequest(requestUrl, options = {}) {
  const zoneId = getStoredZoneId();
  const res = await fetch(requestUrl, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(zoneId ? { "X-Zone-Id": zoneId } : {}),
      ...options.headers,
    },
  });

  const text = await res.text();
  let json = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch (_) {
    json = null;
  }

  if (!res.ok) {
    const rawMessage =
      (json && (json.message || json.Message || json.error)) ||
      `Request failed (${res.status})`;
    const message = friendlyErrorMessage(rawMessage);
    const err = new Error(message);
    err.status = res.status;
    err.data = json;
    if (res.status === 401 && typeof window !== "undefined") {
      try {
        window.dispatchEvent(new CustomEvent("auth:expired"));
      } catch (_) {
        /* ignore */
      }
    }
    throw err;
  }

  if (typeof window !== "undefined") {
    try {
      const hZone = res.headers.get("X-Zone-Id");
      const hCurr = res.headers.get("X-Zone-Currency");
      if (hZone || hCurr) {
        window.dispatchEvent(
          new CustomEvent("zone:server-echo", {
            detail: { zoneId: hZone, currency: hCurr },
          }),
        );
      }
    } catch (_) {
      /* ignore */
    }
  }

  return json;
}
